import { ref } from 'vue';
import ApiService from '../services/api.service';
import AlertService from '../services/errors.service';

export default function useAnnouncements() {
  const responseResult = ref(false);
  const featureEnabled = ref(true);

  const _handleFeatureGate = (error) => {
    if (error?.response?.status === 403 && error?.response?.data?.can_access === false) {
      featureEnabled.value = false;
    }
  };

  // ── Consumer ──────────────────────────────────────────────────────────────

  const getModalAnnouncements = async () => {
    try {
      const response = await ApiService.get('/announcements/modal');
      return response.data;
    } catch (error) {
      _handleFeatureGate(error);
      return { data: [] };
    }
  };

  const getAnnouncements = async (query = {}) => {
    try {
      const response = await ApiService.get('/announcements?' + new URLSearchParams(query));
      return response.data;
    } catch (error) {
      _handleFeatureGate(error);
      return null;
    }
  };

  const getAnnouncement = async (id) => {
    try {
      const response = await ApiService.get('/announcements/' + id);
      return response.data;
    } catch {
      return null;
    }
  };

  const acknowledgeAnnouncement = async (id) => {
    try {
      const response = await ApiService.post('/announcements/' + id + '/acknowledge');
      return response.data;
    } catch {
      return null;
    }
  };

  // ── Management ────────────────────────────────────────────────────────────

  const getManageAnnouncements = async (query = {}) => {
    try {
      const response = await ApiService.get('/announcements/manage?' + new URLSearchParams(query));
      return response.data;
    } catch (error) {
      _handleFeatureGate(error);
      return null;
    }
  };

  const createAnnouncement = async (data) => {
    try {
      const response = await ApiService.post('/announcements/manage', data);
      if (response.status === 200) {
        AlertService.successMessage(response.data.message || 'Announcement created');
        responseResult.value = true;
        return response.data;
      }
    } catch (error) {
      _handleFeatureGate(error);
      responseResult.value = false;
      throw error;
    }
  };

  const getManageAnnouncement = async (id) => {
    try {
      const response = await ApiService.get('/announcements/manage/' + id);
      return response.data;
    } catch (error) {
      _handleFeatureGate(error);
      return null;
    }
  };

  // The backend update route only responds to a spoofed PUT (multipart
  // form-data with `_method=PUT`) — a plain JSON POST body is silently
  // ignored. Normalize any caller's payload into that shape.
  const toUpdateFormData = (data) => {
    if (data instanceof FormData) {
      if (!data.has('_method')) data.append('_method', 'PUT');
      return data;
    }
    const fd = new FormData();
    fd.append('_method', 'PUT');
    Object.entries(data ?? {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => fd.append(`${key}[]`, v));
      } else {
        fd.append(key, value);
      }
    });
    return fd;
  };

  const updateAnnouncement = async (id, data) => {
    try {
      const response = await ApiService.post('/announcements/manage/' + id, toUpdateFormData(data));
      if (response.status === 200) {
        AlertService.successMessage(response.data.message || 'Announcement updated');
        responseResult.value = true;
        return response.data;
      }
    } catch (error) {
      _handleFeatureGate(error);
      responseResult.value = false;
      throw error;
    }
  };

  const deleteAnnouncement = async (id) => {
    try {
      const response = await ApiService.delete('/announcements/manage/' + id);
      if (response.status === 200) {
        AlertService.successMessage(response.data.message || 'Announcement deleted');
        responseResult.value = true;
        return response.data;
      }
    } catch (error) {
      _handleFeatureGate(error);
      responseResult.value = false;
      throw error;
    }
  };

  const getAcknowledgments = async (id, query = {}) => {
    try {
      const response = await ApiService.get(
        '/announcements/manage/' + id + '/acknowledgments?' + new URLSearchParams(query)
      );
      return response.data;
    } catch (error) {
      _handleFeatureGate(error);
      return null;
    }
  };

  const audiencePreview = async (data) => {
    try {
      const response = await ApiService.post('/announcements/manage/audience-preview', data);
      return response.data;
    } catch (error) {
      _handleFeatureGate(error);
      // Let the caller see and display validation errors instead of silently
      // swallowing them — a failed preview should never look like "no data".
      throw error;
    }
  };

  return {
    responseResult,
    featureEnabled,
    getModalAnnouncements,
    getAnnouncements,
    getAnnouncement,
    acknowledgeAnnouncement,
    getManageAnnouncements,
    createAnnouncement,
    getManageAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    getAcknowledgments,
    audiencePreview
  };
}